import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { cn } from '@rocco/utils/cn';
import { ReactComponent as EyeIcon } from '@rocco/icons/svg/eye.svg';
import { ReactComponent as LikeIcon } from '@rocco/icons/svg/like.svg';
import { shortenCount } from '@rocco/utils/format-number';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import { TileMedia } from '@rocco/ui/tile-shared/components/TileMedia';
import { TileBadge } from '@rocco/ui/tile-shared/components/TileBadge';
import { TileFollowButton } from '@rocco/ui/tile-shared/components/TileFollowButton';
import { ProjectTileCounterBadge } from './ProjectTileCounterBadge';
import { uiContextAtomTileActions } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';
import { useTracker } from '../../../../../tracker';
import { trackTileMediaDetailClick } from '../../../../../web/app/modules/tracking/util/trackTileMedia';
import { OptionalLink } from '@rocco/components/link/OptionalLink';
import { TILE_COMPONENTS_CLASS_NAMES } from '@rocco/ui/tile-shared/utils/contstants';
import {
    PROJECT_HERO_TRANSITION_NAME,
    setPendingHeroImage,
    usePrefersReducedMotion,
    useTileHeroViewTransitionStyle,
} from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';
import { uiContextAtomViewTransitionsEnabled } from '@rocco/states/uiContextAtomViewTransitions';
import { useTileViewTransitionScope } from '@rocco/contexts/TileViewTransitionScopeContext';
import { useTileBannerMenuRegistry } from '@rocco/contexts/TileBannerMenuContext';

const PROJECT_TILE_MIN_VIEW_COUNT_THRESHOLD = 1;
const PROJECT_TILE_MIN_SAVED_COUNT_THRESHOLD = 100;

export const ProjectTile = (props: TileViewModel<ProjectTileViewModel>) => {
    const {
        aspectRatio,
        model: project,
        section,
        enableTileSlider,
        isFollowed,
        onFollow,
        routeState,
        hideFollowButton = false,
        followButton,
        overlay,
        inlineActionButtons,
        disableLink = false,
        onClick,
        className,
        contentEditable,
        eager,
        rounded = true,
        imageSizes,
        compactBrowseText = false,
    } = props;
    // 5.0 directory browse: landscape tile (592/430) with the larger detail-page
    // typography (22px title + professional) sitting UNDER the image, matching the
    // project detail page's tiles rather than the default 16px layout.
    const isBrowse = aspectRatio === 'projectBrowse';
    const tileActions = useAtomValue(uiContextAtomTileActions);
    const tileRef = useRef<HTMLAnchorElement>(null);
    const tracker = useTracker();
    // Flag AND surface scope: only tiles on listing surfaces (category /
    // browse pages wrap their content in TileViewTransitionScope) take part —
    // a tile on a detail page or the homepage navigates plainly. Both hooks
    // run unconditionally (no short-circuit) to keep the hook order stable.
    const viewTransitionsFlagOn = useAtomValue(
        uiContextAtomViewTransitionsEnabled,
    );
    const inTileTransitionScope = useTileViewTransitionScope();
    // Reduced motion opts out of the whole flow — no view transition (no
    // snapshot capture at all) and no app-mode chrome; the page behaves
    // exactly like a plain flag-off navigation.
    const prefersReducedMotion = usePrefersReducedMotion();
    const appModeEnabled =
        viewTransitionsFlagOn && inTileTransitionScope && !prefersReducedMotion;
    const viewTransitionsEnabled = appModeEnabled;
    const heroStyle = useTileHeroViewTransitionStyle(
        disableLink || !viewTransitionsEnabled ? undefined : project.link,
        PROJECT_HERO_TRANSITION_NAME,
    );

    // Banner checkbox: optimistic selection seeded from the current banner slide.
    const onSetBanner = tileActions?.trackingConfig?.onSetBanner;
    const canEditBanner = project.canEdit ?? false;
    const initialBannerImageId = useMemo(
        () =>
            project.images.find((image) => image.isBanner)?.libraryImageId ??
            null,
        [project.images],
    );
    const [selectedBannerImageId, setSelectedBannerImageId] = useState<
        number | null
    >(initialBannerImageId);
    const [bannerSubmitting, setBannerSubmitting] = useState(false);
    // Re-sync the optimistic selection when fresh loader data arrives (the
    // banner slide changed). We never trigger our own revalidation after a
    // click, so this only fires on a genuine reload — not mid-submit.
    useEffect(() => {
        setSelectedBannerImageId(initialBannerImageId);
    }, [initialBannerImageId]);
    // Track the visible carousel slide so the admin-menu action targets it.
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const currentImage = project.images[currentSlideIndex];
    const showTileMediaSlider =
        enableTileSlider && !project.video?.length && project.images.length > 1;
    // Offer the banner action only for editors, when the feature is enabled
    // (onSetBanner is injected behind the flag), only when the tile media slider
    // is visible, and only when banner data was actually loaded for this project
    // — otherwise we cannot tell a removed slide from one we just did not enrich.
    const currentLibraryImageId = currentImage?.libraryImageId ?? null;
    const offerBannerAction =
        canEditBanner &&
        !!onSetBanner &&
        currentLibraryImageId != null &&
        showTileMediaSlider &&
        (project.bannerDataLoaded ?? false);

    const handleSelectBanner = useCallback(
        (libraryImageId: number) => {
            if (!onSetBanner || libraryImageId === selectedBannerImageId) {
                return;
            }
            const previous = selectedBannerImageId;
            // Optimistically move the check; ES is eventually consistent so we
            // do not revalidate — local state is the session source of truth.
            setSelectedBannerImageId(libraryImageId);
            setBannerSubmitting(true);
            onSetBanner({
                libraryItemID: project.id,
                libraryImageID: libraryImageId,
                onError: () => {
                    setSelectedBannerImageId(previous);
                    setBannerSubmitting(false);
                },
                onSuccess: () => setBannerSubmitting(false),
            });
        },
        [onSetBanner, selectedBannerImageId, project.id],
    );

    // Publish the action for the wrapping AdminContextMenu (right-click). It
    // re-registers on every slide change so the menu always acts on the slide
    // the admin is looking at, and clears when the action is unavailable.
    const bannerMenu = useTileBannerMenuRegistry();
    useEffect(() => {
        if (!bannerMenu) return;
        if (!offerBannerAction || currentLibraryImageId == null) {
            bannerMenu.register(null);
            return;
        }
        bannerMenu.register({
            isCurrentBanner: selectedBannerImageId === currentLibraryImageId,
            setCurrentAsBanner: () => {
                if (!bannerSubmitting) {
                    handleSelectBanner(currentLibraryImageId);
                }
            },
        });
        return () => bannerMenu.register(null);
    }, [
        bannerMenu,
        offerBannerAction,
        currentLibraryImageId,
        selectedBannerImageId,
        bannerSubmitting,
        handleSelectBanner,
    ]);

    const handleTileClick = () => {
        if (bannerSubmitting) {
            return;
        }
        onClick?.();
        if (viewTransitionsEnabled && !disableLink) {
            // Scoped to the media box: overlay badges (logo, banner checkbox)
            // are <img>s too and must never become the underlay.
            setPendingHeroImage(
                project.link,
                tileRef.current?.querySelector<HTMLImageElement>(
                    '.rocco-tile-media img',
                )?.currentSrc,
            );
        }
        tileActions?.trackingConfig?.onClick?.(
            tileRef,
            project.id,
            project.professional.id,
            section,
        );
        trackTileMediaDetailClick(tracker, {
            itemID: project.id,
            itemType: 'ProjectTile',
            itemName: project.title,
        });
    };

    return (
        <OptionalLink
            ref={tileRef}
            mode={disableLink ? 'override' : 'normal'}
            to={project.link}
            prefetch="intent"
            viewTransition={viewTransitionsEnabled}
            state={
                appModeEnabled
                    ? { ...routeState, detailTransition: true }
                    : routeState
            }
            aria-busy={bannerSubmitting || undefined}
            onClick={(event) => {
                if (bannerSubmitting) {
                    event.preventDefault();
                    event.stopPropagation();
                    return;
                }
                handleTileClick();
            }}
        >
            <div
                className={cn(
                    'flex flex-col transition-opacity duration-300',
                    isBrowse ? 'gap-2.5' : 'gap-4',
                    className,
                    bannerSubmitting && 'pointer-events-none opacity-50',
                )}
            >
                {/* The morphing box for the tile→detail view transition. The
                    hover/save overlays inside are part of the snapshot, which
                    is fine — the new page's crop dissolves over it early in
                    the flight. */}
                <div
                    className={cn(
                        'group relative',
                        aspectRatio === 'featured' ? 'h-full' : undefined,
                    )}
                    style={heroStyle}
                >
                    <TileMedia
                        aspectRatio={aspectRatio}
                        images={project.images}
                        video={project.video}
                        enableTileSlider={enableTileSlider}
                        limitImageCount={false}
                        rounded={rounded}
                        eager={eager}
                        sizes={imageSizes}
                        onSlideChange={(direction, currentIndex) => {
                            setCurrentSlideIndex(currentIndex);
                            tileActions?.trackingConfig?.onTileMediaSliderSlideChange?.(
                                direction,
                                project.id,
                            );
                        }}
                    />

                    {overlay ? (
                        overlay
                    ) : (
                        <>
                            <div
                                className={cn(
                                    'absolute inset-0 hidden bg-gray-900/10 opacity-0 transition-opacity duration-300 md:block md:group-hover:opacity-100',
                                    rounded && 'rounded-tile',
                                )}
                            />
                            <div className="absolute top-3.5 right-4 flex gap-1 transition-opacity duration-300 md:opacity-0 md:group-hover:opacity-100">
                                {followButton
                                    ? followButton
                                    : !hideFollowButton && (
                                          <TileFollowButton
                                              pinId={project.id}
                                              pinType="ProjectPin"
                                              isFollowed={isFollowed}
                                              onFollow={onFollow}
                                          />
                                      )}
                            </div>
                        </>
                    )}

                    {/* Badge max-w keeps it clear of the 42px save control at
                        2-up mobile widths; Badge ellipsises natively. */}
                    {project.badge && (
                        <div className="absolute top-3 left-4 flex transition-opacity duration-300 max-md:max-w-[calc(100%-5rem)] md:group-hover:opacity-0">
                            <TileBadge title={project.badge} />
                        </div>
                    )}
                </div>

                <div className="flex flex-col gap-4">
                    {inlineActionButtons && (
                        <div className="flex flex-col">
                            {inlineActionButtons}
                        </div>
                    )}
                    <div
                        className={cn(
                            TILE_COMPONENTS_CLASS_NAMES.TILE_DESCRIPTION_BLOCK,
                            'flex flex-col gap-0.5',
                        )}
                    >
                        <span
                            contentEditable={contentEditable?.enabled.title}
                            role={
                                contentEditable?.enabled.title
                                    ? 'textbox'
                                    : undefined
                            }
                            aria-label={
                                contentEditable?.enabled.title
                                    ? 'Edit title'
                                    : undefined
                            }
                            aria-multiline="false"
                            suppressContentEditableWarning={true}
                            onInput={(e: React.FormEvent<HTMLSpanElement>) =>
                                contentEditable?.onChange?.(
                                    (e.target as HTMLSpanElement).textContent ??
                                        '',
                                    'title',
                                )
                            }
                            onBlur={() => {
                                contentEditable?.onBlur?.();
                            }}
                            onKeyDown={(
                                e: React.KeyboardEvent<HTMLSpanElement>,
                            ) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    (e.target as HTMLSpanElement).blur();
                                }
                            }}
                            className={cn(
                                isBrowse
                                    ? cn(
                                          // Below md, match the compact
                                          // design-board tile title scale
                                          // (text-4.5/leading-none) instead of
                                          // the detail-page-sized 22px title.
                                          // md+ shows 22px, stepping down to
                                          // 16px when the grid asks for compact
                                          // browse text (5-6 column Display).
                                          'overflow-hidden font-medium text-ellipsis text-white max-md:text-[0.75rem] max-md:leading-[1.2]',
                                          compactBrowseText
                                              ? 'md:text-[1.375rem] md:leading-[1.3] lg:text-[1rem]'
                                              : 'md:text-[1.375rem] md:leading-[1.3]',
                                      )
                                    : 'overflow-hidden text-sm font-medium tracking-[0.03rem] text-ellipsis md:text-base md:tracking-[0.02rem]',
                                !contentEditable?.enabled.title &&
                                    // Browse tiles wrap the title to 2 lines below
                                    // md (reserving 2 lines' height so rows align:
                                    // 2 × 1.125rem × 1 leading-none); md+ (and
                                    // non-browse) stay single-line nowrap.
                                    (isBrowse
                                        ? 'text-nowrap max-md:line-clamp-2 max-md:whitespace-normal'
                                        : 'text-nowrap'),
                                contentEditable?.enabled.title &&
                                    'focus-within:border-primary-500 -mx-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 hover:border-gray-500',
                            )}
                        >
                            {project.title}
                        </span>
                        <span
                            contentEditable={
                                contentEditable?.enabled.description
                            }
                            role={
                                contentEditable?.enabled.description
                                    ? 'textbox'
                                    : undefined
                            }
                            aria-label={
                                contentEditable?.enabled.description
                                    ? 'Edit description'
                                    : undefined
                            }
                            aria-multiline="false"
                            suppressContentEditableWarning={true}
                            onInput={(e: React.FormEvent<HTMLSpanElement>) =>
                                contentEditable?.onChange?.(
                                    (e.target as HTMLSpanElement).textContent ??
                                        '',
                                    'description',
                                )
                            }
                            onBlur={() => {
                                contentEditable?.onBlur?.();
                            }}
                            onKeyDown={(
                                e: React.KeyboardEvent<HTMLSpanElement>,
                            ) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    (e.target as HTMLSpanElement).blur();
                                }
                            }}
                            className={cn(
                                isBrowse
                                    ? cn(
                                          // Quiet meta scale below md, matching
                                          // the design-board card's "Created by"
                                          // density; colour stays white/40
                                          // (dark-themed browse card) — md+
                                          // tracks the title size (22px, or
                                          // 16px in compact browse).
                                          'overflow-hidden font-normal text-ellipsis text-white/40 max-md:text-[0.75rem] max-md:leading-[1.3]',
                                          compactBrowseText
                                              ? 'md:text-[1.375rem] md:leading-[1.3] lg:text-[1rem]'
                                              : 'md:text-[1.375rem] md:leading-[1.3]',
                                      )
                                    : 'overflow-hidden text-sm font-medium tracking-[0.03rem] text-ellipsis text-gray-900/40 md:text-base md:tracking-[0.02rem]',
                                !contentEditable?.enabled.description &&
                                    'text-nowrap',
                                contentEditable?.enabled.description &&
                                    'focus-within:border-primary-500 -ml-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 hover:border-gray-500',
                            )}
                        >
                            {project.professional.name}
                        </span>
                    </div>

                    <div
                        className={cn(
                            'flex justify-start gap-2.25 empty:hidden md:pb-[0.325rem]',
                            isBrowse && 'hidden',
                        )}
                    >
                        {!!project.viewedCount &&
                            project.viewedCount >
                                PROJECT_TILE_MIN_VIEW_COUNT_THRESHOLD && (
                                <ProjectTileCounterBadge
                                    icon={EyeIcon}
                                    value={shortenCount(
                                        project.viewedCount ?? 0,
                                    )}
                                />
                            )}
                        {!!project.savedCount &&
                            project.savedCount >
                                PROJECT_TILE_MIN_SAVED_COUNT_THRESHOLD && (
                                <ProjectTileCounterBadge
                                    icon={LikeIcon}
                                    value={shortenCount(
                                        project.savedCount ?? 0,
                                    )}
                                />
                            )}
                    </div>
                </div>
            </div>
        </OptionalLink>
    );
};
